import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../sheet";
import ImportDialog, { ExportButton } from "./ImportDialog";

/**
 * Import and export for the whole space.
 *
 * The counterpart to CollectionTransfer, which files the tree under the open
 * collection; here there is no target, so collections in the file become roots
 * and loose records go to "Without collection".
 *
 * Owner-only, but it does not ask useIsOwnSpace(): the entry lives in the user
 * menu and is about the signed-in user no matter whose space is on screen. The
 * endpoints agree — with no target collection, import and export fall back to
 * the session user.
 */
export default function SpaceTransferSheet({
  children,
  open,
  onOpenChange,
}: {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const me = useAppSelector(selectMe);
  if (!me) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}

      <SheetContent
        className="max-sm:inset-0 max-sm:w-full max-sm:border-l-0 sm:w-[380px] sm:max-w-[380px] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="px-5 pt-[18px] pb-3 border-b">
          <SheetTitle
            className="text-[18px] font-semibold text-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Import and export
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-3 px-5 pt-4 pb-5">
          <p className="text-[12px] text-muted-foreground">
            Add a tree of collections and records from a JSON file, filed at the
            top of your collections. Exporting writes out everything you own.
          </p>
          <ImportDialog />
          <ExportButton />
        </div>
      </SheetContent>
    </Sheet>
  );
}
